/**
 * Google Enhanced Ecommerce Object
 *
 * Helper functionality for Google Enhanced Ecommerce.
 *
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const util = {

    /**
     * Get the time different in seconds between now and clientTimeThen
     *
     * @param  {int}  clientTimeThen time in seconds
     *
     * @returns  {int}  Number of seconds since clientTimeThen
     */
    getTimeDifference: (clientTimeThen) => (
        Math.floor(Date.now() / 1000) - clientTimeThen
    ),

    /**
     * Get the number of seconds until the promo expires
     *
     * @param  {int}  clientTimeOnLoad timestamp (in seconds) when the module was initialised
     * @param  {int}  serverTimeOnLoad timestamp (in seconds) when the server rendered the response
     * @param  {int}  offerExpiryTime timestamp (in seconds) when the offer expires
     *
     * @returns  {int}  Number of seconds until the offer expires
     */
    getSecondsUntilPromoEnd: (clientTimeOnLoad, serverTimeOnLoad, offerExpiryTime) => (
        offerExpiryTime - (
            serverTimeOnLoad + gaEcommerce.util.getTimeDifference(clientTimeOnLoad)
        )
    ),

    /**
     * Adds the timeUntilPromoExpires property to productData
     *
     * @param  {string|int}  offerExpiry the date when then offer expires
     * @param  {object}  productData the product data object
     *
     * @returns  {object} productData
     */
    includeTimeUntilPromoExpires: (offerExpiry, productData) => {
        if (typeof(offerExpiry) !== 'undefined') {
            let offerExpiryTime = new Date(offerExpiry);

            // Check date is valid
            if (!isNaN(offerExpiryTime)) {

                offerExpiryTime.setDate(offerExpiryTime.getDate() + 1);
                let offerExpiryTimeSeconds = offerExpiryTime.getTime() / 1000;

                productData.timeUntilPromoExpires = gaEcommerce.util.getSecondsUntilPromoEnd(
                    gaEcommerce.clientTimeOnLoad,
                    parseFloat(document.body.dataset.currentServerTimestamp),
                    offerExpiryTimeSeconds
                );
            }
        }

        return productData;
    },

    /**
     * dataExists - Check if a property of an object exists and isn't empty
     *
     * @param  {object}  dataObject
     * @param  {object}  propertyName
     *
     * @returns  {boolean}  True if property exists, false otherwise
     */
    dataExists: (dataObject, propertyName) => 
        typeof dataObject === 'object'
        && dataObject !== null
        && dataObject.hasOwnProperty(propertyName)
        && dataObject[propertyName].length > 0 
    ,

    /**
     * Sums the prices for all products within the array
     *
     * @param  {array}  products
     * @returns  {string}  The decimal total to 2 decimal places
     */
    calculateTotalOrderPrice: (products) => {
        let totalPrice = 0.00;

        for (var i = 0; i < products.length; i++) {
            const product = products[i];

            if (typeof product === 'string') {
                product = gaEcommerce.util.parseJsonObj(product);
            }

            totalPrice += parseFloat(product.price);
        }

        return totalPrice.toFixed(2);
    },

    /**
     * Caches the selection
     *
     * @param  {object}  product data
     * @returns  {void}
     */
    cacheSelection: (product) => {
        clientData.storage.setData({
            name: 'ecomSelection',
            value: JSON.stringify(product)
        }, 'sessionStorage');
    },

    /**
     * Get any pre-signup selection from sessionStorage
     *
     * @returns {string|null}
     */
    getSelection: () =>
        sessionStorage.getItem('ecomSelection')
    ,

    /**
     * Clear any pre-signup selection from sessionStorage
     *
     * @returns {void}
     */
    clearSelection: () =>
        sessionStorage.removeItem('ecomSelection')
    ,

    /**
     * Get's the current viewport top and bottom
     *
     * @returns {{top: int, bottom: int}}
     */
    getViewport: () => ({
        top: window.scrollY,
        bottom: window.scrollY + window.innerHeight
    }),

    /**
     * Check if an element is in the viewPort
     *
     * @param  {object} DOM element
     *
     * @returns {boolean}
     */
    inViewport: (element) => 
        gaEcommerce.util.inRange(element, gaEcommerce.util.getViewport())
    ,

    /**
     * Check if the element is in the top/bottom range
     *
     * @param {object} element DOM element
     * @param {{top: int, bottom: int}} range
     * @param {number} portion of image that needs to be in view
     *
     * @returns {boolean}
     */
    inRange: (element, range, portion) => {
        if (typeof portion === 'undefined') {
            // default 1% of the element must be in view
            portion = 0.01;
        }

        const elementOuterHeight = gaEcommerce.util.outerHeight(element),
              elementTopOffset = gaEcommerce.util.offset(element).top;

        const topPortion = {
            top: elementTopOffset,
            bottom: elementTopOffset + (elementOuterHeight * portion)
        };

        const bottomPortion = {
            top: elementTopOffset + ((1 - portion) * elementOuterHeight),
            bottom: elementTopOffset + elementOuterHeight
        };

        return (topPortion.top > range.top && topPortion.bottom < range.bottom)
            || (bottomPortion.top > range.top && bottomPortion.bottom < range.bottom);
    },

    /**
     * Is element visible and in the range
     *
     * @param {object} DOM element
     * @param {{top: int, bottom: int}} range
     * @param {number} portion of image that needs to be in view
     *
     * @returns {boolean}
     */
    isSeenInRange: (element, range, portion) => (
        gaEcommerce.util.isVisible(element)
        && gaEcommerce.util.inRange(element, range, portion)
    ),

    /**
     * Is the element visible and in the view port
     *
     * @param {object} DOM element
     * @returns {boolean}
     */
    isSeen: (element) => {
        return (
            gaEcommerce.util.isVisible(element)
            && gaEcommerce.util.inViewport(element)
        );
    },

    /**
     * Check if an element is visible
     *
     * @param {object} DOM element
     * @returns {boolean}
     */
    isVisible: (element) => 
        (element.offsetWidth > 0 || element.offsetHeight > 0)
        && getComputedStyle(element).opacity !== '0'
    ,

    /**
     * Add's a hashCode string prototype
     *
     * @returns {void}
     */
    initHashCodePrototype: () => {
        // Create a reusable hashcode function to convert html or strings to hashes
        String.prototype.hashCode = function () {

            let hash = 0;

            if (this.length === 0) return hash;

            for (let i = 0; i < this.length; i++) {
                hash = ((hash<<5)-hash)+this.charCodeAt(i);
                hash = hash & hash; // Convert to 32bit integer
            }

            return (hash >>> 0); // Always return a positive int
        };
    },

    /**
     * Throttle a callback function so it's not called too much
     *
     * @param {function} fn The function to throttle
     * @param {int} threshhold
     * @param {object} scope The context
     *
     * @returns {function}
     */
    throttle: (fn, threshhold, scope) => {

        threshhold || (threshhold = 250);

        let last, deferTimer;

        return (...args) => {
            const context = scope || window,
                  now = Date.now();

            if (last && now < last + threshhold) {

                // Hold on to it
                clearTimeout(deferTimer);

                deferTimer = setTimeout(() => {
                    last = now;
                    fn.apply(context, args);
                }, (threshhold + last) - now);
            } else {
                last = now;
                fn.apply(context, args);
            }
        };
    },

    /**
     * Remove an element from a list if it exists
     *
     * @param {*} element
     * @param {array} list
     *
     * @returns {void}
     */
    removeFromList: (element, list) => {
        const index = list.indexOf(element);

        if (index >= 0) {
            list.splice(index, 1);
        }
    },

    /**
     * Get a string matcher with the given string
     *
     * @param {string} match
     * @returns {function(string): boolean}
     */
    getStringMatcher: (match) => 
        (subject) => (subject === match)
    ,

    /**
     * Get a regex matcher with the given pattern
     *
     * @param {string} pattern
     * @returns {function(string): boolean}
     */
    getRegexMatcher: (pattern) =>
        (subject) => new RegExp(pattern).test(subject)
    ,

    /**
     * Implements Object.assign for IE11
     * Takes any number of objects as parameters
     *
     * @returns {object}
     */
    mergeObject: (...args) => {
        // :( IE11 doesn't support Object.assign (sigh) :(
        if (typeof Object.assign === "function") {
            return Object.assign.apply(null, args);
        }

        const original = args[0];

        for (var i = 1; i < args.length; i++) {
            const object = args[i];

            Object.keys(object).map(function (field) {
                original[field] = object[field];
            });
        }

        return original;
    },

    /**
     * Curries a that checks if an item is in the array
     *
     * @param {array} array
     * @returns {function(*=): boolean}
     */
    inArrayFilter: (array) =>
        (item) => (array.indexOf(item) >= 0)
    ,

    /**
     * Helper function that trims the input string
     *
     * @param string
     * @returns {*}
     */
    trim: (string) =>
        string.trim()
    ,

    /**
     * Check if a nested object is defined
     *
     * @param {object} object
     * @param {array} properties
     * @returns {boolean}
     */
    isDefined: (object, properties) => {
        if (typeof object === 'undefined') {
            return false;
        }

        object = properties.reduce(function(object, property) {
            if (typeof object === 'undefined' || typeof object[property] === 'undefined') {
                return undefined;
            }

            return object[property];
        }, object);

        return typeof object !== 'undefined';
    },

    /**
     * Check if we're on the account change journey
     *
     * @returns {boolean}
     */
    isAccountChange: () => (
        gaEcommerce.core.getJourney() === 'broadband|update|residential'
    ),

    /**
     * Check if we're on the legacy account change journey
     *
     * @returns {boolean}
     */
    isLegacyAccountChange: () => (
        gaEcommerce.core.getJourney() === 'broadband|change-product|residential'
    ),

    /**
     * Check if we're on the res acquisition journey
     *
     * @returns {boolean}
     */
    isResAcquisition: () =>
        gaEcommerce.core.getJourney() === 'broadband|sign-up|residential'
    ,

    /**
     * Return the appropriate basket for the current journey
     *
     * @returns {object}
     */
    getBasket: () =>
        gaEcommerce.util.isAccountChange() || gaEcommerce.util.isLegacyAccountChange()
            ? gaEcommerce.accountChangeBasket
            : gaEcommerce.basket
    ,

    /*
     * Get the rendered height of a given element
     *
     * @param {object} el DOM element
     * @param {boolean} includeMargins Include margins in the calculation
     *
     * @returns {object}
     */
    outerHeight: (el, includeMargins = false) => {
        let height = el.offsetHeight;
        const style = getComputedStyle(el);

        return includeMargins
            ? height + parseInt(style.marginTop) + parseInt(style.marginBottom)
            : height;
    },

    /*
     * Get the coordinates of a given element element relative to the document.
     *
     * @param {object} el DOM element
     * @returns {object}
     */
    offset: (el) => {
        const rect = el.getBoundingClientRect();

        return { 
            top: rect.top + window.scrollY, 
            left: rect.left + window.scrollX
        };
    },

    /*
     * Convert a json string to an object and handle errors
     *
     * @param {string} jsonString
     * @returns {object}
     */
    parseJsonObj: (jsonString) => {
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            return {};
            console.warn('[gaEcommerce.js] Failed to parse json');
        }
    },

    /*
     * Convert a json string to an array and handle errors
     *
     * @param {string} jsonString
     * @returns {array}
     */
    parseJsonArray: (jsonString) => {
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            return [];
            console.warn('[gaEcommerce.js] Failed to parse json');
        }
    }
};

export default util;

